/**
 * dpwalk.mjs — binding her 3D parts to her DP units, and the stations of the walk.
 *
 * FOUNDER, 2026-08-20: "now do the 3d walk along her".
 *
 * ── THE MODEL IS ALREADY THERE, AND IT DECLARES ITS OWN GAPS ──────────────────────────────────
 * /api/ship/model serves 38 parts and 1,415 quads compiled from her table of offsets, her declared
 * scantlings and her arrangement — including both engine rooms, the centreline bulkhead, all four
 * generating sets, both switchboards and all five thrusters. It also carries `known_not_drawn` and
 * `not_modelled`, which is the rarest thing a 3D model can have: a list of what it is not.
 *
 * ── THE BINDING IS A SEARCH, SO ITS COMPLETENESS IS ASSERTED IN BOTH DIRECTIONS ───────────────
 * A 3D part carries `id, label, layer, explode, opacity, why, citation`. It does NOT carry the
 * switchboard it belongs to — that is stated in prose inside `why` ("feeding the PORT switchboard"),
 * and prose is not a field. The authoritative mapping lives on the DP model, where every thruster
 * and generator declares `switchboard`.
 *
 * So the binding matches part ids against DECLARED unit ids. That is a search, and this estate's
 * rule is that where a mapping is declared you read it rather than searching — here it is not
 * declared, so the next-best discipline applies: the search must fail LOUDLY and in both
 * directions. `bind()` returns `unbound` (parts matching no unit) and `missing` (units with no
 * part), and gate-dp101-labs asserts both are empty. A part that stops matching, or a unit that
 * gains no geometry, turns the gate red rather than quietly vanishing from the walk — which is the
 * failure mode that hid the switchboards from the ch3 candidate list a few hours ago.
 *
 * ── WHY A WALK AND NOT A FLY-AROUND ───────────────────────────────────────────────────────────
 * Chapter 3's subject is that redundancy is a designed answer to a designed question, and its
 * boundary is a place. A free camera lets a learner look at a pretty ship. A walk along the chain —
 * switchboard, its generators, its thrusters, then the bulkhead that separates them from the other
 * half — is the chapter's argument, in the order the chapter makes it.
 */

/** Her declared units, from the DP model, keyed by the id the 3D part ids are built from. */
export const unitsOf = (dpModel) => [
  ...(dpModel.thrusters || []).map((t) => ({ id: t.id, kind: 'thruster', kw: t.kw, switchboard: t.switchboard, type: t.type })),
  ...(dpModel.generators || []).map((g) => ({ id: g.id, kind: 'generator', kw: g.kw, switchboard: g.switchboard, type: 'generating set' })),
];

/* A part id embeds its unit id: `azimuth_az-p`, `bow_tunnel_bt-1`, `dg1`. Matched on the WHOLE
   trailing token so `dg1` cannot match `dg11` if she ever grows one — a substring match here would
   be the `/ais/` fault again, in a different file. */
const idMatches = (partId, unitId) => {
  const p = String(partId).toLowerCase();
  const u = String(unitId).toLowerCase();
  return p === u || p.endsWith(`_${u}`);
};

/**
 * Bind parts to units, and report BOTH failure directions rather than either silently.
 * Switchboards are bound separately: they are named in the DP model only as strings, and their
 * geometry is `switchboard_port` / `switchboard_stbd`.
 */
export function bind(model3d, dpModel) {
  const units = unitsOf(dpModel);
  const parts = model3d.parts || [];
  const boards = (dpModel.switchboards || []).map((b) => (typeof b === 'string' ? b : b.id));

  const byUnit = new Map();
  for (const u of units) {
    const part = parts.find((p) => idMatches(p.id, u.id));
    if (part) byUnit.set(u.id, { unit: u, part });
  }
  const boardPart = new Map();
  for (const b of boards) {
    const part = parts.find((p) => p.id.toLowerCase() === `switchboard_${b.toLowerCase()}`);
    if (part) boardPart.set(b, part);
  }

  /* UNBOUND is only asserted over parts that could plausibly be a unit — an equipment or fittings
     part. The shell, the deck and the mud tanks are not units and must not be reported as if the
     binding had failed on them, or the signal drowns in noise it was never about. */
  const unitLayers = new Set(['equipment', 'fittings']);
  const claimed = new Set([...byUnit.values()].map((x) => x.part.id));
  for (const p of boardPart.values()) claimed.add(p.id);
  const loose = parts.filter((p) => unitLayers.has(p.layer) && !claimed.has(p.id));

  /* AND THE OTHER DIRECTION IS NOT SIMPLY "MUST BE EMPTY", because it never can be. Five parts bind
     to no unit and all five are legitimate: `skeg` is structure, and `slew_gear_az-p`,
     `bt_motor_bt-1` and their siblings are SUB-COMPONENTS of thrusters already bound. Asserting an
     empty list would have been asserting something false and then loosening the assertion until it
     passed — the exact move this estate calls loosening a gate to make it green.
     So they are classified instead. A part whose id carries a bound unit's id is a sub-component,
     derived rather than listed. Anything else is `unexplained`, and every part carries a `why`, so
     an unexplained part with no `why` is the real fault worth failing on: a piece of geometry in an
     equipment layer that neither binds to a unit nor says what it is. */
  const boundIds = [...byUnit.keys()].map((x) => String(x).toLowerCase());
  const isSub = (p) => boundIds.some((u) => String(p.id).toLowerCase().includes(u));
  const subcomponents = loose.filter(isSub).map((p) => p.id);
  const unexplained = loose.filter((p) => !isSub(p) && !String(p.why || '').trim()).map((p) => p.id);
  const standalone = loose.filter((p) => !isSub(p)).map((p) => p.id);

  return {
    byUnit, boardPart, boards,
    missing: units.filter((u) => !byUnit.has(u.id)).map((u) => u.id),
    missingBoards: boards.filter((b) => !boardPart.has(b)),
    subcomponents, standalone, unexplained,
  };
}

/**
 * THE STATIONS. Built from her boards rather than listed, so a vessel with three switchboards walks
 * three sides without anybody editing this file.
 *
 * The bulkhead station is the point of the whole walk: it is where the two halves stop being a
 * diagram. Its part is found by layer and id rather than named here, and if she declares none the
 * station is dropped with a reason instead of pointing the camera at nothing.
 */
export function stations(model3d, dpModel, b = null) {
  const bnd = b || bind(model3d, dpModel);
  const out = [{ id: 'overview', name: 'On deck', focus: null,
    says: 'Her whole form, compiled from her table of offsets. Everything below is inside this hull.' }];

  for (const board of bnd.boards) {
    const sbPart = bnd.boardPart.get(board);
    const gens = [...bnd.byUnit.values()].filter((x) => x.unit.kind === 'generator' && x.unit.switchboard === board);
    const thr = [...bnd.byUnit.values()].filter((x) => x.unit.kind === 'thruster' && x.unit.switchboard === board);
    out.push({ id: `board:${board}`, name: `${board} switchboard`, focus: sbPart ? [sbPart.id] : [],
      says: sbPart ? sbPart.why : 'No geometry is declared for this board.',
      units: sbPart ? [board] : [] });
    out.push({ id: `gens:${board}`, name: `${board} generating sets`, focus: gens.map((x) => x.part.id),
      says: `${gens.length} set(s) feed this board: ${gens.map((x) => `${x.unit.id} at ${x.unit.kw} kW`).join(', ') || 'none declared'}.`,
      units: gens.map((x) => x.unit.id) });
    out.push({ id: `thr:${board}`, name: `${board} thrusters`, focus: thr.map((x) => x.part.id),
      says: `${thr.length} unit(s) hang off this board: ${thr.map((x) => `${x.unit.id} (${x.unit.type}, ${x.unit.kw} kW)`).join(', ') || 'none declared'}. Lose the board and you lose all of them together.`,
      units: thr.map((x) => x.unit.id) });
  }

  const bulkhead = (model3d.parts || []).find((p) => /centreline_bulkhead/.test(p.id));
  if (bulkhead) {
    out.push({ id: 'boundary', name: 'The boundary', focus: [bulkhead.id], units: [],
      says: bulkhead.why || bulkhead.label });
  }
  return out;
}

export const WALK_WHY =
  'The walk follows the redundancy chain in the order chapter 3 argues it — a switchboard, the sets '
  + 'that feed it, the thrusters that hang off it, and then the bulkhead that separates that half '
  + 'from the other. A free camera would let you look at a handsome ship; this is the argument.';

export const MODEL_HONESTY =
  'A three-dimensional view is the most persuasive surface in this whole course, and persuasion is '
  + 'exactly what a fabricated fact needs. So her model publishes what it is NOT — parts known not '
  + 'to be drawn, and things deliberately not modelled — and those lists are shown here rather than '
  + 'left in the file.';
